import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Params} from '@angular/router';
import {
  AppConfigService,
  DictionaryBaseDto,
  DictionaryBaseService,
  DocumentService,
  GrowlService,
  InquiryService,
  LoggedUserService,
  MasterPolicyContractService,
  PolicyContractPreviewDto,
  PolicyContractVersionService,
  PolicyLimitListService,
  ProductLimitListVersionCriteriaDto,
  ProductLimitListVersionSimpleDto,
  PropertyService,
  RepositoryService,
  RouterService,
  SearchDataProvider,
  TemplateService,
  TemplateSimpleDto,
  UserDto,
} from '../../bonding_shared';
import {PolicyContractViewSection, PolicyContractViewService} from './services/policy-contract-view.service';
import {BusinessUtils} from '../../bonding_shared/utils/business-utils';
import {PolicyContractGuiService} from './services/policy-contract-gui.service';
import {DetailsView} from '../../bonding_shared/components/details-view/details-view';
import {
  AppProperty,
  BusinessObjectType,
  ContactNoteElementaryRight,
  Language,
  LimitRequestType,
  PolicyContractType,
  PolicyContractVersionStatus,
  PolicyElementaryRight,
  RepoDocumentElementaryRight,
  ServiceContactElementaryRight,
  ServiceNoteElementaryRight,
} from '../../bonding_shared/model/dictionary-ids';
import {ServiceNoteService} from '../service-notes/service-note.service';

@Component({
  selector: 'policy-contract-preview',
  templateUrl: './policy-contract-preview.component.pug',
})
export class PolicyContractPreviewComponent extends DetailsView implements OnInit {
  data: PolicyContractPreviewDto;
  policyContractVersionId: number;
  policyLimitListDataProvider: SearchDataProvider<ProductLimitListVersionCriteriaDto, ProductLimitListVersionSimpleDto>;
  longLabel = false;
  bonusMalusRequestTypes: DictionaryBaseDto[] = [];
  templates: TemplateSimpleDto[] = [];
  readonly BusinessObjectType = BusinessObjectType;
  readonly LimitRequestType = LimitRequestType;
  readonly ContactNoteElementaryRight = ContactNoteElementaryRight;
  readonly ServiceContactElementaryRight = ServiceContactElementaryRight;
  readonly ServiceNoteElementaryRight = ServiceNoteElementaryRight;
  readonly RepoDocumentElementaryRight = RepoDocumentElementaryRight;
  readonly PolicyElementaryRight = PolicyElementaryRight;
  readonly PolicyContractVersionStatus = PolicyContractVersionStatus;
  readonly PolicyContractType = PolicyContractType;
  viewSection = PolicyContractViewSection;
  readonly AppProperty = AppProperty;
  serviceNotesCount: number;
  isNewLimitDisable: boolean;

  statProperties = [
    {name: 'policyContract.preview.stats.policyYear', property: 'policyYearFormatted', datatype: 'policyLink'},
    {name: 'policyContract.preview.stats.policySubYear', property: 'policySubYearFormatted'},
    {name: 'common.validFrom', property: 'validFrom', datatype: 'date'},
    {name: 'common.validTo', property: 'validTo', datatype: 'date'},
    {name: 'policyContract.preview.stats.annualLOL', property: 'annualLOL', datatype: 'currency'},
    {name: 'policyContract.preview.stats.globalLOL', property: 'globalLOL', datatype: 'currency'},
    {name: 'policyContract.preview.stats.minPremium', property: 'minPremium', datatype: 'currency'},
    {name: 'policyContract.preview.stats.insuranceCover', property: 'insuranceCover', datatype: 'percent'},
    {name: 'policyContract.preview.stats.premiumPaid', property: 'premiumPaid', datatype: 'currency'},
    {name: 'policyContract.preview.stats.claimsPaid', property: 'claimsPaid', datatype: 'currency'},
    {name: 'policyContract.preview.stats.claimsReserve', property: 'claimsReserves', datatype: 'currency'},
    {name: 'policyContract.preview.stats.nql', property: 'nql', datatype: 'currency'},
    {name: 'policyContract.preview.stats.eeMinimumRetention', property: 'eeOrMinimumRetention', datatype: 'currency'},
    {name: 'policyContract.preview.stats.aad', property: 'aad', datatype: 'currency'},
    {name: 'policyContract.preview.stats.turnover', property: 'turnOver', datatype: 'currency'},
  ];

  constructor(
    private policyContractVersionService: PolicyContractVersionService,
    private route: ActivatedRoute,
    public router: RouterService,
    private policyContractViewService: PolicyContractViewService,
    private policyLimitListService: PolicyLimitListService,
    protected masterPolicyContractService: MasterPolicyContractService,
    private userService: LoggedUserService,
    protected growlService: GrowlService,
    public appService: AppConfigService,
    protected dictionaryBaseService: DictionaryBaseService,
    protected inquiryService: InquiryService,
    protected templateService: TemplateService,
    protected repositoryService: RepositoryService,
    public documentService: DocumentService,
    protected guiService: PolicyContractGuiService,
    private propertyService: PropertyService,
    private serviceNote: ServiceNoteService
  ) {
    super(growlService);
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => this.initializeView(params));
    this.longLabel = this.isLongLabel();
  }

  isLongLabel(): boolean {
    const user: UserDto = this.userService.getLoggedUserData();
    return user && user.language && (user.language.id === Language.GERMAN || user.language.id === Language.POLISH);
  }

  initializeView(params: Params) {
    this.policyContractVersionId = +params['id'];
    this.policyContractVersionService.getPreview(this.policyContractVersionId).subscribe((res) => {
      this.data = res;
      this.serviceNote.counts$.subscribe((counts) => {
        if (counts[this.data.masterPolicyId] !== undefined) {
          this.serviceNotesCount = counts[this.data.masterPolicyId];
        } else {
          this.serviceNotesCount = 0;
        }
      });
      if (this.data?.masterPolicyId) {
        this.masterPolicyContractService.getMasterPolicyContract(this.data.masterPolicyId).subscribe((masterPolicy) => {
          const isCurrentYearUserError = this.data?.status?.id === PolicyContractVersionStatus.USER_ERROR;

          const hasFutureValidYear =
            masterPolicy?.policyContracts?.reduce((lastVersionYear, currentYear) =>
              currentYear.policyYear > lastVersionYear.policyYear ? currentYear : lastVersionYear
            )?.lastVersionStatus?.id !== PolicyContractVersionStatus.USER_ERROR;

          this.isNewLimitDisable = isCurrentYearUserError && !hasFutureValidYear;
        });
      }
      this.serviceNote.getNotesList(BusinessObjectType.MASTER_POLICY, this.data.masterPolicyId);
      if (this.guiService.justCreatedPolicy && this.guiService.justCreatedPolicy.id === this.policyContractVersionId) {
        this.serverErrors = this.guiService.justCreatedPolicy.infos;
      }
      this.guiService.justCreatedPolicy = undefined;
      this.initializePolicyLimitListDataProvider();
    });
    this.dictionaryBaseService
      .getDictionaryBase('BonusMalusRequestType')
      .subscribe((r) => (this.bonusMalusRequestTypes = r));
  }

  yearTitle(index: number) {
    if (index > 0) {
      return 'CY - ' + index;
    }
    return 'CY';
  }

  style() {
    switch (this.data.stats.length) {
      case 4:
        return 'width: 20%';
      case 3:
        return 'width: 26%';
      case 2:
        return 'width: 40%';
      default:
        return 'width: 80%';
    }
  }

  initializePolicyLimitListDataProvider() {
    this.policyLimitListDataProvider = BusinessUtils.createPolicyLimitListDataProvider(this.policyLimitListService);
    this.policyLimitListDataProvider.textSearch = false;
    this.policyLimitListDataProvider.searchCriteria.criteria.limitList.masterPolicyContract.id =
      this.data.masterPolicyId;
  }

  createNewRenewalInquiry() {
    this.inquiryService.createRenewalInquiry(this.policyContractVersionId).subscribe({
      next: (inquiryVersionId) => this.router.toPolicyInquiryDetails(inquiryVersionId),
      error: () => this.growlService.error('policyContract.preview.newRenewalInquiryError'),
    });
  }

  showCession(): boolean {
    return this.isSectionVisible(PolicyContractViewSection.POLICY_CONTRACT_DATA__CESSION, true);
  }

  toPolicyServiceContacts() {
    this.router.toServiceContacts({
      boTypeId: BusinessObjectType.MASTER_POLICY,
      boId: this.data.masterPolicyId,
      returnBoTypeId: BusinessObjectType.POLICY_VERSION,
      returnBoId: this.policyContractVersionId,
    });
  }

  repoBOTypeId(): number {
    return Number(this.propertyService.properties[AppProperty.BOTYPE_FOR_POLICIES]);
  }

  repoBOId(): number {
    switch (this.propertyService.properties[AppProperty.BOTYPE_FOR_POLICIES]) {
      case BusinessObjectType.MASTER_POLICY:
        return this.data.masterPolicyId;
      case BusinessObjectType.POLICY:
        return this.data.policyId;
      case BusinessObjectType.POLICY_VERSION:
        return this.policyContractVersionId;
    }
  }

  isSectionVisible(
    viewSection: PolicyContractViewSection,
    defaultVisibility = false,
    globalConditionsOfInsuranceId = null
  ) {
    if (this.data && this.data.contractType && this.data.contractType.id) {
      return this.policyContractViewService.isSectionVisible(
        this.data.contractType.id,
        viewSection,
        defaultVisibility,
        globalConditionsOfInsuranceId
      );
    }
  }

  isNewLimitButtonDisable() {
    return this.isNewLimitDisable;
  }
}
